import * as THREE from 'three';
import { EnvironmentBuilder } from '@/render/Environment';
import { buildTrophy } from '@/ui/title/trophy';
import { CITIES } from '@/config/cities';

/**
 * RaceCup.ts — M158c (owner: "the trophies when the user wins need to be a 3D
 * object, much more modern looking like the cars; it should spin lightly").
 *
 * The RACE cup: a modern gold / silver / bronze chalice for a top-3 race
 * finish. The World Tour Cup (ui/title/trophy.ts) stays the TOUR champion's
 * trophy, so the two never blur together: this one is a tall faceted tulip
 * bowl on a slim stem, two swept wing handles, a checkered racing band round
 * the bowl, and a black lacquer plinth carrying an engraved place plate.
 *
 * It renders on its own small transparent canvas (the TitleStage pattern),
 * spins slowly with a breath of bob, and frees its GPU context on stop().
 */
export type CupMetal = 'gold' | 'silver' | 'bronze';

const METALS: Record<CupMetal, { color: number; dark: string; light: string; sheen: number }> = {
  gold: { color: 0xffc24a, dark: '#5a3c08', light: '#ffe7a0', sheen: 0xffd98a },
  silver: { color: 0xe4e9ef, dark: '#3c434c', light: '#ffffff', sheen: 0xdfe8ff },
  bronze: { color: 0xd98a52, dark: '#4a240c', light: '#ffc79c', sheen: 0xffb889 },
};

const SPIN = 0.42; // rad/s — a light turn, never a showroom spin
/** The race cup's height (plinth foot to lip) — the camera frames by it. */
const CUP_HEIGHT = 3.3;

/** The engraved place plate (1ST / 2ND / 3RD) in the cup's metal. */
function plateTexture(label: string, m: (typeof METALS)[CupMetal]): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 96;
  const x = c.getContext('2d');
  if (x) {
    const g = x.createLinearGradient(0, 0, 0, 96);
    g.addColorStop(0, m.light);
    g.addColorStop(0.5, '#' + m.color.toString(16).padStart(6, '0'));
    g.addColorStop(1, m.dark);
    x.fillStyle = g;
    x.fillRect(0, 0, 256, 96);
    x.fillStyle = 'rgba(0,0,0,0.55)';
    x.font = '900 italic 58px system-ui, sans-serif';
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillText(label, 128, 52);
    x.strokeStyle = 'rgba(0,0,0,0.35)';
    x.lineWidth = 4;
    x.strokeRect(4, 4, 248, 88);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** The checkered racing band (metal squares on dark lacquer). */
function checkerTexture(m: (typeof METALS)[CupMetal]): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 32;
  const x = c.getContext('2d');
  if (x) {
    const sq = 16;
    for (let i = 0; i < 512 / sq; i++) {
      for (let j = 0; j < 2; j++) {
        x.fillStyle = (i + j) % 2 === 0 ? m.light : '#0d0e12';
        x.fillRect(i * sq, j * sq, sq, sq);
      }
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = THREE.RepeatWrapping;
  return t;
}

/** Build the cup (origin at the plinth's foot, ~3.6 tall). */
export function buildRaceCup(metal: CupMetal, place: string): THREE.Group {
  const m = METALS[metal];
  const group = new THREE.Group();
  const metalMat = new THREE.MeshPhysicalMaterial({
    color: m.color,
    metalness: 1,
    roughness: 0.22,
    envMapIntensity: 2.1,
  });
  const lacquer = new THREE.MeshPhysicalMaterial({
    color: 0x0b0c10,
    metalness: 0.2,
    roughness: 0.28,
    clearcoat: 1,
    clearcoatRoughness: 0.08,
  });

  // Plinth: an octagonal lacquer block, a metal band under its top edge, a
  // metal cap the stem rises from.
  const plinth = new THREE.Mesh(new THREE.CylinderGeometry(0.92, 1.06, 0.62, 8), lacquer);
  plinth.position.y = 0.31;
  plinth.rotation.y = Math.PI / 8;
  group.add(plinth);
  const band = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 0.95, 0.07, 8), metalMat);
  band.position.y = 0.5;
  band.rotation.y = Math.PI / 8;
  group.add(band);
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.86, 0.14, 32), metalMat);
  cap.position.y = 0.69;
  group.add(cap);
  // The engraved plate on the plinth's front face.
  const plate = new THREE.Mesh(
    new THREE.PlaneGeometry(0.92, 0.34),
    new THREE.MeshPhysicalMaterial({
      map: plateTexture(place, m),
      metalness: 0.85,
      roughness: 0.3,
    }),
  );
  plate.position.set(0, 0.25, 0.985);
  group.add(plate);

  // Stem + bowl: one lathe — a fluted foot, a slim waist, then the tulip bowl
  // flaring to a rolled lip (12 facets so the metal catches crisp highlights).
  const profile: [number, number][] = [
    [0.0, 0.76],
    [0.5, 0.76],
    [0.42, 0.84],
    [0.18, 0.98],
    [0.13, 1.25],
    [0.12, 1.5],
    [0.2, 1.68],
    [0.42, 1.86],
    [0.62, 2.1],
    [0.76, 2.42],
    [0.84, 2.78],
    [0.9, 3.08],
    [0.97, 3.2],
    [0.93, 3.25],
    [0.82, 3.12],
    [0.7, 2.9],
  ];
  const pts = profile.map(([r, y]) => new THREE.Vector2(r, y));
  const bowl = new THREE.Mesh(new THREE.LatheGeometry(pts, 12), metalMat);
  bowl.material = metalMat.clone();
  (bowl.material as THREE.MeshPhysicalMaterial).side = THREE.DoubleSide;
  group.add(bowl);
  // A collar knot at the waist.
  const knot = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.05, 12, 32), metalMat);
  knot.rotation.x = Math.PI / 2;
  knot.position.y = 1.36;
  group.add(knot);

  // The checkered racing band round the bowl.
  const checker = checkerTexture(m);
  checker.repeat.set(1, 1);
  const bandMesh = new THREE.Mesh(
    new THREE.CylinderGeometry(0.715, 0.655, 0.16, 48, 1, true),
    new THREE.MeshPhysicalMaterial({
      map: checker,
      metalness: 0.7,
      roughness: 0.25,
      clearcoat: 0.8,
    }),
  );
  bandMesh.position.y = 2.2;
  group.add(bandMesh);

  // Two swept wing handles (speed lines, not the classic loop).
  for (const s of [-1, 1]) {
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(s * 0.55, 1.95, 0),
      new THREE.Vector3(s * 1.08, 2.05, 0),
      new THREE.Vector3(s * 1.28, 2.55, 0),
      new THREE.Vector3(s * 1.12, 3.0, 0),
      new THREE.Vector3(s * 0.9, 3.12, 0),
    ]);
    const tube = new THREE.Mesh(new THREE.TubeGeometry(curve, 40, 0.06, 10, false), metalMat);
    group.add(tube);
    // a fin trailing back off each handle
    const fin = new THREE.Mesh(
      new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3([
          new THREE.Vector3(s * 1.2, 2.3, 0),
          new THREE.Vector3(s * 1.42, 2.62, -0.05),
          new THREE.Vector3(s * 1.5, 2.95, -0.1),
        ]),
        20,
        0.035,
        8,
        false,
      ),
      metalMat,
    );
    group.add(fin);
  }
  return group;
}

/** The spinning cup on its own small canvas. */
export class RaceCupStage {
  private renderer: THREE.WebGLRenderer | null = null;
  private env: EnvironmentBuilder | null = null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(26, 1, 0.1, 50);
  private readonly spin = new THREE.Group();
  private raf = 0;
  private last = 0;
  private t = 0;

  constructor(
    private readonly host: HTMLElement,
    private readonly reduced: boolean,
  ) {}

  /** A top-3 race finish: the gold / silver / bronze race cup. */
  start(metal: CupMetal, place: string): void {
    this.startObject(buildRaceCup(metal, place), CUP_HEIGHT, true);
  }

  /** The tour champion: the World Tour Cup itself, wearing the cup ball. */
  async startTourCup(): Promise<void> {
    // the globe lives in its own lazy chunk (the title loads it the same way)
    const { buildEarth, GLOBE_R } = await import('@/world/Globe');
    const trophy = buildTrophy();
    const ball = buildEarth(CITIES, 'ball');
    ball.group.scale.setScalar(trophy.globeRadius / GLOBE_R);
    ball.group.position.copy(trophy.cradle);
    trophy.group.add(ball.group);
    // shared caches (globe skin, trophy textures): never disposed from here —
    // dropping this canvas's GL context frees what it uploaded.
    this.startObject(trophy.group, trophy.height, false);
  }

  private owned = false;

  private startObject(object: THREE.Object3D, height: number, owned: boolean): void {
    this.stop();
    this.owned = owned;
    let r: THREE.WebGLRenderer;
    try {
      r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      return; // no WebGL left: the screen still reads without the cup
    }
    r.setClearColor(0x000000, 0);
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    r.domElement.className = 'rs-cup-canvas';
    this.host.appendChild(r.domElement);
    this.renderer = r;
    this.env = new EnvironmentBuilder(r);
    this.scene.environment = this.env.generate({
      // A studio sweep: metal is only as bright as what it mirrors (a dark
      // dome turned the gold cup brown — measured on the first render).
      skyTop: 0x5a6478,
      skyHorizon: 0xd8b070,
      sun: 0xfff0d0,
      sunIntensity: 6,
      sunDir: new THREE.Vector3(-0.5, 0.7, 0.6).normalize(),
      ground: 0x1a140c,
    });
    this.scene.add(new THREE.HemisphereLight(0xc8d4ec, 0x141008, 1.1));
    const key = new THREE.DirectionalLight(0xfff0d0, 3.2);
    key.position.set(-5, 8, 7);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x9fb8ff, 1.6);
    rim.position.set(6, 4, -6);
    this.scene.add(rim);
    // A warm flood close in front (the title trophy's trick) for hot highlights.
    const flood = new THREE.PointLight(0xffe2b0, 90, 30, 2);
    flood.position.set(2.2, height * 0.9, height * 1.6);
    this.scene.add(flood);
    this.spin.add(object);
    this.spin.rotation.y = -0.5;
    this.scene.add(this.spin);
    // Frame by height: the object fills ~80% of the canvas, seen a touch from above.
    const dist = (height * 1.42) / (2 * Math.tan(THREE.MathUtils.degToRad(13)));
    this.camera.position.set(0, height * 0.62, dist);
    this.camera.lookAt(0, height * 0.48, 0);
    this.resize();
    this.last = performance.now();
    const tick = (now: number): void => {
      this.raf = requestAnimationFrame(tick);
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      if (!this.reduced) {
        this.t += dt;
        this.spin.rotation.y += SPIN * dt;
        this.spin.position.y = Math.sin(this.t * 1.6) * 0.04;
      }
      this.resize();
      this.renderer?.render(this.scene, this.camera);
    };
    this.raf = requestAnimationFrame(tick);
  }

  private resize(): void {
    const r = this.renderer;
    if (!r) return;
    const w = Math.max(1, this.host.clientWidth);
    const h = Math.max(1, this.host.clientHeight);
    const c = r.domElement;
    if (
      c.width !== Math.round(w * r.getPixelRatio()) ||
      c.height !== Math.round(h * r.getPixelRatio())
    ) {
      r.setSize(w, h, false);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    }
  }

  stop(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.owned)
      this.spin.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.geometry.dispose();
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const mat of mats) {
            const mm = mat as THREE.MeshPhysicalMaterial;
            mm.map?.dispose();
            mm.dispose();
          }
        }
      });
    this.spin.clear();
    for (const child of [...this.scene.children]) if (child !== this.spin) this.scene.remove(child);
    this.env?.dispose();
    this.env = null;
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer.domElement.remove();
      this.renderer = null;
    }
  }
}
